import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { animated, config, useSpring } from '@react-spring/web';
import { withScope } from '@sentry/core';
import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import styled, { keyframes } from 'styled-components';

import { Rarity, WheelType } from '@sorare/core/src/__generated__/globalTypes';
import { Impact } from '@sorare/core/src/atoms/animations/Impact';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { CloseButton } from '@sorare/core/src/atoms/buttons/CloseButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconTicket } from '@sorare/core/src/atoms/icons/system/iconTicket.ts';
import { iconX } from '@sorare/core/src/atoms/icons/system/iconX.ts';
import { Centered } from '@sorare/core/src/atoms/layout/Centered';
import { Drawer } from '@sorare/core/src/atoms/layout/Drawer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL } from '@sorare/core/src/atoms/typography';
import {
  ANY_SPORT_HOME,
  ANY_SPORT_SERIES,
} from '@sorare/core/src/constants/__generated__/routes';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useSeriesNav } from '@sorare/core/src/hooks/commonGame/useSeriesNav';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useTransitionApi } from '@sorare/core/src/hooks/ui/useTransitionApi';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useShortcut } from '@sorare/core/src/hooks/useShortcut';
import { useWheelTicketDistributionEndedByRarity } from '@sorare/core/src/hooks/useWheelTicketDistributionEndedByRarity';
import { shuffle } from '@sorare/core/src/lib/arrays';
import { getRarityColor } from '@sorare/core/src/lib/colors';
import { sendSafeError } from '@sorare/core/src/lib/error';
import { glossary, wheel } from '@sorare/core/src/lib/glossary';
import { withFragments } from '@sorare/core/src/lib/gql';
import { getSafePreviousLocation } from '@sorare/core/src/lib/navigation/getSafePreviousLocation';
import {
  ALL_WHEEL_CURRENCIES,
  GOLDEN_WHEEL_TICKET_BY_RARITY,
  WHEEL_TICKET_BY_RARITY,
} from '@sorare/core/src/lib/wheelCurrencies';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { Background } from './Background/index.tsx';
import { BoostedOdds } from './BoostedOdds/index.tsx';
import { GetMoreTickets } from './GetMoreTickets/index.tsx';
import { GoldenTickets } from './GoldenTickets/index.tsx';
import { Odds } from './Odds/index.tsx';
import { Reward } from './Reward/index.tsx';
import { Slot } from './Slot/index.tsx';
import { Spinner } from './Spinner/index.tsx';
import { Tickets } from './Tickets/index.tsx';
import { WheelRaritySwitcher } from './WheelRaritySwitcher/index.tsx';
import type {
  SpinWheelMutation,
  SpinWheelMutationVariables,
  WheelQuery,
  WheelQueryVariables,
  isWorthyOfCelebration_ProbabilisticBundleSlotConfig,
} from './__generated__/index.graphql.ts';

const openingAnimation = keyframes`
  from {
    opacity: 0;
    transform: translateY(-2%) scale(0.95);
  }
`;

const Root = styled.div`
  animation: 0.2s ease-out ${openingAnimation} forwards;
`;

const Wrapper = styled(Vertical)`
  position: relative;
  isolation: isolate;
  width: 100%;
  align-items: stretch;
  min-height: var(--100vh);
  @media ${tabletAndAbove} {
    overflow: hidden;
  }

  & > :first-child {
    position: absolute;
    inset: 0;
    z-index: -1;
  }
`;

const Grid = styled.div`
  flex-grow: 1;
  width: 100%;
  display: grid;
  overflow: hidden;
  align-content: stretch;
  align-items: center;
  justify-items: center;
  grid-template-areas:
    'header header header'
    'wheel wheel wheel';
  grid-template-rows: min-content 1fr;

  @media ${tabletAndAbove} {
    min-height: var(--100vh);
    grid-template-areas:
      'header header header'
      'wheel wheel wheel'
      'buttons buttons buttons';
    grid-template-rows: 1fr min-content 1fr;
  }
`;

const Header = styled(animated.div)<{ $withGetMoreTickets?: boolean }>`
  grid-area: header;
  display: grid;
  width: 100%;
  height: 100%;
  align-content: stretch;
  align-items: stretch;
  ${({ $withGetMoreTickets }) =>
    $withGetMoreTickets
      ? `grid-template-columns: 1fr 1fr;
      grid-template-areas:
    'title title'
    'getMoreTickets tickets'
    'golden golden'
  `
      : `
      grid-template-columns: 1fr;
      grid-template-areas:
    'title'
    'tickets'
    'golden'
  `};
  grid-template-rows: min-content min-content min-content;

  > * {
    padding: var(--intermediate-unit);
    border-color: var(--c-nd-200);
    border-width: 0 0 1px;
    border-style: solid;

    &:nth-child(3) {
      border-width: 0 0 1px 1px;
    }
    &:not(:first-child) {
      backdrop-filter: blur(10px);
    }
  }
  @media ${tabletAndAbove} {
    ${({ $withGetMoreTickets }) =>
      $withGetMoreTickets
        ? `
        grid-template-columns: 1fr 1fr 1fr;
        grid-template-areas:
        'title title title'
        'getMoreTickets tickets golden';`
        : `
        grid-template-columns: 1fr 1fr;
        grid-template-areas:
        'title title'
        'tickets golden';`};
    > * {
      border-width: 0 0 1px;
      &:nth-child(3) {
        border-width: 0 1px 1px;
      }
    }
  }
`;

const Footer = styled(animated(Horizontal)).attrs({
  center: true,
})``;

const FooterRoot = styled(Vertical)`
  grid-area: buttons;
  position: sticky;
  bottom: 0;
  z-index: 1;
  overflow: hidden;
  padding: var(--double-unit);
  &.nonInteractive {
    pointer-events: none;
  }
`;

const StyledDrawer = styled(Drawer)`
  width: 100%;
  overflow-y: auto;
  @media ${tabletAndAbove} {
    width: 500px;
  }
`;

const SwitcherWrapper = styled(animated.div)`
  position: absolute;
  bottom: 0;
  left: 20%;
`;

const LogoTitle = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const isWorthyOfCelebration = withFragments(
  (probableConfig?: isWorthyOfCelebration_ProbabilisticBundleSlotConfig) => {
    if (!probableConfig) {
      return false;
    }
    if (
      probableConfig.deliverableItems?.length ||
      probableConfig.cards?.length ||
      probableConfig.cardShards?.length ||
      probableConfig.conversionCredit
    ) {
      return true;
    }

    // not just tickets or golden tickets
    return probableConfig.inGameCurrencies?.some(
      x => !ALL_WHEEL_CURRENCIES.includesValue(x.currency)
    );
  },
  {
    ProbabilisticBundleSlotConfig: gql`
      fragment isWorthyOfCelebration_ProbabilisticBundleSlotConfig on ProbabilisticBundleSlotConfig {
        id
        deliverableItems {
          quantity
        }
        cards {
          quality
        }
        cardShards {
          id
        }
        inGameCurrencies {
          id
          currency
        }
        conversionCredit {
          id
        }
      }
    ` as TypedDocumentNode<isWorthyOfCelebration_ProbabilisticBundleSlotConfig>,
  }
);

const query = gql`
  query WheelQuery($sport: Sport!, $rarity: Rarity!) {
    config {
      id
    }
    market {
      id
      myWheelTasks(sport: $sport, rarity: $rarity) {
        id
        ...GetMoreTickets_taskInterface
      }
      activeWheelConfig(sport: $sport, rarity: $rarity) {
        slug
        title
        logoUrl
        dailyClaimTask
        goldenWheelCost: wheelCost(wheelType: GOLDEN) {
          quantity
        }
        wheelOdds(wheelType: STANDARD) {
          id
          slots {
            probableConfigs {
              id
              ...Slot_probabilisticBundleSlotConfig
              ...Reward_probabilisticBundleSlotConfig
              ...isWorthyOfCelebration_ProbabilisticBundleSlotConfig
            }
          }
        }
        goldenWheelOdds: wheelOdds(wheelType: GOLDEN) {
          id
          slots {
            probableConfigs {
              id
              ...Slot_probabilisticBundleSlotConfig
              ...Reward_probabilisticBundleSlotConfig
              ...isWorthyOfCelebration_ProbabilisticBundleSlotConfig
            }
          }
        }
      }
    }
  }
  ${Slot.fragments.ProbabilisticBundleSlotConfig}
  ${Reward.fragments.probabilisticBundleSlotConfig}
  ${GetMoreTickets.fragments.taskInterface}
  ${isWorthyOfCelebration.fragments.ProbabilisticBundleSlotConfig}
` as TypedDocumentNode<WheelQuery, WheelQueryVariables>;

const spinWheelMutation = gql`
  mutation SpinWheelMutation(
    $sport: Sport!
    $wheelType: WheelType!
    $rarity: Rarity!
  ) {
    spinWheel(
      input: { sport: $sport, wheelType: $wheelType, rarity: $rarity }
    ) {
      errors {
        code
        message
        path
      }
      reward {
        id
        drawnSlotConfigIds
        items {
          id
          ...Reward_probabilisticBundleSlotItemInterface
        }
      }
      currentUser {
        slug
        wheelTickets: inGameCurrencyBalances(
          sport: $sport
          inGameCurrencies: [
            WHEEL_TICKET
            COMMON_WHEEL_TICKET
            RARE_WHEEL_TICKET
            SUPER_RARE_WHEEL_TICKET
            UNIQUE_WHEEL_TICKET
          ]
        ) {
          amount
          currency
        }
        goldenWheelTickets: inGameCurrencyBalances(
          sport: $sport
          inGameCurrencies: [
            GOLDEN_WHEEL_TICKET
            COMMON_GOLDEN_WHEEL_TICKET
            RARE_GOLDEN_WHEEL_TICKET
            SUPER_RARE_GOLDEN_WHEEL_TICKET
            UNIQUE_GOLDEN_WHEEL_TICKET
          ]
        ) {
          amount
          currency
        }
      }
    }
  }
  ${Reward.fragments.probabilisticBundleSlotItemInterface}
` as TypedDocumentNode<SpinWheelMutation, SpinWheelMutationVariables>;

type Props = {
  rarity: Rarity;
};

const WheelComponent = ({ rarity }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const navigate = useNavigate();
  const { isIosApp, postMessage } = useIsMobileApp();
  const onClose = () => {
    if (isIosApp) {
      postMessage('closeCurrentWebview');
      /* We should not add a return here because in some flows iOS does nothing with this message and continues inside the webview. */
    }
    navigate(
      getSafePreviousLocation(
        rarity === Rarity.common
          ? generateSportContextPath(ANY_SPORT_SERIES)
          : generateSportContextPath(ANY_SPORT_HOME)
      )
    );
  };
  const isTabletAndAbove = useIsTabletAndAbove();
  const {
    so5: { dailyTasksExpiresAt },
  } = useConfigContext();

  const { currentUser } = useCurrentUserContext();
  const nbTickets =
    currentUser?.wheelTickets.find(
      x => x.currency === WHEEL_TICKET_BY_RARITY[rarity]
    )?.amount ?? 0;
  const currentNbGoldenTickets =
    currentUser?.goldenWheelTickets.find(
      x => x.currency === GOLDEN_WHEEL_TICKET_BY_RARITY[rarity]
    )?.amount ?? 0;
  const [nbGoldenTickets, setNbGoldenTickets] = useState(
    currentNbGoldenTickets
  );
  const [wheelType, setWheelType] = useState<WheelType | undefined>(undefined);
  const [hasOpened, setHasOpened] = useState(false);
  const close = useCallback(() => setWheelType(undefined), [setWheelType]);
  const { sport } = useSportContext();
  const { data } = useQuery(query, {
    variables: {
      sport,
      rarity,
    },
  });

  const useDailyTask = data?.market?.activeWheelConfig?.dailyClaimTask;

  const dailyWheelTicketTask = useDailyTask
    ? data?.market?.myWheelTasks[0]
    : undefined;

  const goldenWheelCost =
    data?.market?.activeWheelConfig?.goldenWheelCost?.quantity ?? 10;

  const wheelOdds = data?.market?.activeWheelConfig?.wheelOdds;

  const goldenWheelOdds = data?.market?.activeWheelConfig?.goldenWheelOdds;

  const [spinWheel] = useMutation(spinWheelMutation, {
    showErrorsWithSnackNotification: true,
  });
  const [spinning, setSpinning] = useState(false);
  const spinningRef = useRef(false);
  const [immediate, setImmediate] = useState(false);
  const cancelDelayRef = useRef<(() => void) | null>(null);

  const [isRewardShown, setIsRewardShown] = useState(false);
  const [showUI, setShowUI] = useState(true);

  const { updateDOM } = useTransitionApi();
  const toggleReward = useCallback(
    (value: boolean) => {
      updateDOM(() => {
        setIsRewardShown(value);
      });
    },
    [updateDOM]
  );

  const [currentState, setCurrentState] = useState<{
    item?: NonNullable<
      NonNullable<SpinWheelMutation['spinWheel']>['reward']
    >['items'][number];
    index?: number;
    isGoldenWheel: boolean;
    probableConfigs: NonNullable<
      typeof wheelOdds
    >['slots'][number]['probableConfigs'];
  }>({
    item: undefined,
    index: undefined,
    isGoldenWheel: false,
    probableConfigs: [],
  });

  const resetState = useCallback(() => {
    const isGoldenWheel = currentNbGoldenTickets >= goldenWheelCost;
    const currentOdds = isGoldenWheel ? goldenWheelOdds : wheelOdds;

    if (currentOdds?.slots?.[0]?.probableConfigs) {
      if (
        currentState.probableConfigs.length === 0 ||
        isGoldenWheel !== currentState.isGoldenWheel
      ) {
        setCurrentState({
          probableConfigs: currentOdds.slots[0].probableConfigs,
          isGoldenWheel,
          item: undefined,
          index: undefined,
        });
      } else {
        setCurrentState(c => ({
          ...c,
          item: undefined,
          index: undefined,
        }));
      }
    }
  }, [
    currentNbGoldenTickets,
    goldenWheelCost,
    goldenWheelOdds,
    wheelOdds,
    currentState.probableConfigs.length,
    currentState.isGoldenWheel,
  ]);

  if (currentState.probableConfigs.length === 0) {
    resetState();
  }

  // eslint-disable-next-line react-hooks/refs
  spinningRef.current = spinning;

  const skipToResult = useCallback(() => {
    if (!spinningRef.current) return false;
    setImmediate(true);
    cancelDelayRef.current?.();
    return true;
  }, []);

  useEffect(() => {
    const handleSkip = () => {
      skipToResult();
    };

    document.body.addEventListener('pointerdown', handleSkip);

    return () => {
      document.body.removeEventListener('pointerdown', handleSkip);
    };
  }, [skipToResult]);

  useShortcut('Escape', () => {
    if (spinningRef.current) {
      skipToResult();
      return;
    }
    onClose();
  });

  const endSpinUX = () => {
    toggleReward(false);
    setNbGoldenTickets(currentNbGoldenTickets);
    resetState();
    setShowUI(true);
    setImmediate(false);
  };

  const canSpin =
    currentState.probableConfigs.length > 0 &&
    (nbTickets > 0 || currentState.isGoldenWheel);

  const uiSpring = useSpring({
    up: showUI ? 0 : -100,
    down: showUI ? 0 : 100,
    opacity: showUI ? 1 : 0,
    config: config.gentle,
  });

  const spin = async () => {
    // Guard against empty configs - this can happen if data hasn't loaded
    // or if there's a race condition with isGoldenWheel state changes
    if (currentState?.probableConfigs.length === 0) {
      withScope(scope => {
        scope.setExtra('wheelOddsSlots', wheelOdds?.slots);
        scope.setExtra('goldenWheelOddsSlots', goldenWheelOdds?.slots);
        scope.setExtra('isGoldenWheel', currentState.isGoldenWheel);
        sendSafeError(
          new Error('Cannot spin wheel: no probable configs available')
        );
      });
      return;
    }
    setSpinning(true);
    toggleReward(false);
    setShowUI(false);
    const shuffledProbableConfigs = shuffle(currentState.probableConfigs);
    setCurrentState(c => ({
      ...c,
      probableConfigs: shuffledProbableConfigs,
      item: undefined,
      index: undefined,
    }));
    const [{ data: spinWheelData }] = await Promise.all([
      spinWheel({
        variables: {
          sport,
          wheelType: currentState.isGoldenWheel
            ? WheelType.GOLDEN
            : WheelType.STANDARD,
          rarity,
        },
      }).then(result => {
        const hasErrors = (result.data?.spinWheel?.errors.length ?? 0) > 0;
        if (hasErrors) {
          cancelDelayRef.current?.();
        }
        return result;
      }),
      // minimum delay (can be cancelled on error or user tap)
      new Promise<void>(resolve => {
        cancelDelayRef.current = resolve;
        setTimeout(resolve, 1000);
      }),
    ]);

    const hasErrors = (spinWheelData?.spinWheel?.errors.length ?? 0) > 0;

    if (!hasErrors && !spinWheelData?.spinWheel?.reward?.items?.length) {
      throw new Error('Error spinning wheel');
    }

    if (hasErrors) {
      // Violently stop the wheel in case of errors
      setSpinning(false);
      endSpinUX();

      // sort by probability so that the most probable config seems selected
      setCurrentState(c => ({
        ...c,
        probableConfigs: [...c.probableConfigs].sort(
          (a, b) => b.probability - a.probability
        ),
      }));
      return;
    }

    // currentState.probableConfigs has a stale value here, we need to use shuffledProbableConfigs
    const index = shuffledProbableConfigs.findIndex(x =>
      spinWheelData?.spinWheel?.reward?.drawnSlotConfigIds.includes(x.id)
    );

    if (!hasErrors && index === -1) {
      withScope(scope => {
        scope.setExtra(
          'drawnSlotConfigIds',
          spinWheelData?.spinWheel?.reward?.drawnSlotConfigIds
        );
        scope.setExtra('shuffledProbableConfigs', shuffledProbableConfigs);
        scope.setExtra('wheelOddsSlots', wheelOdds?.slots);
        scope.setExtra('goldenWheelOddsSlots', goldenWheelOdds?.slots);
        scope.setExtra('isGoldenWheel', currentState.isGoldenWheel);
        sendSafeError(new Error('Cannot match slot to reward'));
      });
    }

    setCurrentState(c => ({
      ...c,
      item: spinWheelData?.spinWheel?.reward?.items[0],
      index,
    }));
  };

  useShortcut(' ', () => {
    if (isRewardShown) {
      endSpinUX();
      return;
    }
    if (spinningRef.current) {
      skipToResult();
      return;
    }
    if (canSpin) {
      spin().catch(error => {
        sendSafeError(error);
      });
    }
  });

  const isUsingSeries = useSeriesNav();
  const showRaritySwitcher = !isUsingSeries || rarity !== Rarity.common;
  const distributionEndedByRarity = useWheelTicketDistributionEndedByRarity();
  const distributionEnded =
    distributionEndedByRarity[
      rarity as keyof typeof distributionEndedByRarity
    ] ?? false;

  const footer = (
    <FooterRoot
      center
      className={classNames({
        nonInteractive: !showUI,
      })}
    >
      <Footer style={{ y: uiSpring.down, opacity: uiSpring.opacity }}>
        <Button
          color="tertiary"
          size="large"
          onClick={() => {
            setWheelType(
              currentState.isGoldenWheel ? WheelType.GOLDEN : WheelType.STANDARD
            );
            setHasOpened(true);
          }}
        >
          <FormattedMessage {...glossary.rewards} />
        </Button>
        <Button
          size="large"
          color="primary"
          onClick={() => {
            spin().catch(error => {
              sendSafeError(error);
            });
          }}
          loading={spinning}
          disabled={spinning || !canSpin}
        >
          <Impact trigger={currentState.isGoldenWheel}>
            {currentState.isGoldenWheel ? (
              <FormattedMessage defaultMessage="Free Spin" />
            ) : (
              <>
                <FormattedMessage {...wheel.spin} />{' '}
                <span style={{ opacity: 0.5 }}>·</span> 1{' '}
                <FontAwesomeIcon icon={iconX} size="sm" />{' '}
                <FontAwesomeIcon icon={iconTicket} />
              </>
            )}
          </Impact>
        </Button>
      </Footer>
      {!isTabletAndAbove && !!showRaritySwitcher && (
        <animated.div style={{ y: uiSpring.down, opacity: uiSpring.opacity }}>
          <WheelRaritySwitcher rarity={rarity} />
        </animated.div>
      )}
    </FooterRoot>
  );

  const withGetMoreTickets = !!dailyWheelTicketTask;

  return (
    <Wrapper>
      <Background
        golden={currentState.isGoldenWheel}
        rarityColor={
          rarity !== Rarity.common
            ? getRarityColor(rarity, { opacity: 0.5 })
            : undefined
        }
        rarity={rarity}
      />
      <Grid>
        <Header
          style={{ y: uiSpring.up, opacity: uiSpring.opacity }}
          $withGetMoreTickets={withGetMoreTickets}
        >
          <Centered
            style={{
              gridArea: 'title',
              justifySelf: 'stretch',
              padding: 'var(--double-unit)',
            }}
            right={<CloseButton onClose={onClose} size="small" />}
          >
            {data?.market?.activeWheelConfig?.logoUrl ? (
              <LogoTitle
                src={data?.market?.activeWheelConfig?.logoUrl}
                alt={data?.market?.activeWheelConfig?.title}
              />
            ) : (
              <BrandL>{data?.market?.activeWheelConfig?.title}</BrandL>
            )}
          </Centered>
          {!!withGetMoreTickets && (
            <GetMoreTickets
              dailyWheelTicketTask={dailyWheelTicketTask}
              dailyTasksExpiresAt={dailyTasksExpiresAt ?? undefined}
              rarity={rarity}
              distributionEnded={distributionEnded}
            />
          )}
          <Tickets nb={nbTickets} rarity={rarity} />
          <GoldenTickets
            nb={nbGoldenTickets}
            cost={goldenWheelCost}
            onInfoCircle={() => {
              setWheelType(WheelType.GOLDEN);
              setHasOpened(true);
            }}
          />
        </Header>

        <div
          style={{
            gridArea: 'wheel',
            alignSelf: 'stretch',
            justifySelf: 'stretch',
          }}
        >
          <BoostedOdds show={currentState.isGoldenWheel} />
          <Spinner
            spin={spinning}
            stopAtIndex={currentState.index}
            immediate={immediate}
            onSpinEnd={() => {
              setSpinning(false);
              setImmediate(false);
              // is the reward worthy of celebration?
              if (
                currentState.index !== undefined &&
                isWorthyOfCelebration(
                  currentState.probableConfigs[currentState.index]
                )
              ) {
                toggleReward(true);
              } else {
                endSpinUX();
              }
            }}
          >
            {currentState.probableConfigs.map((probableConfig, index) => (
              <Slot
                big
                key={probableConfig.id}
                config={probableConfig}
                style={
                  index === currentState.index && !isRewardShown
                    ? { viewTransitionName: 'wheel-reward' }
                    : {}
                }
                rarity={rarity}
              />
            ))}
          </Spinner>
        </div>

        {!!isRewardShown &&
          !!currentState?.item &&
          currentState?.index !== undefined && (
            <Reward
              rarity={rarity}
              onClose={endSpinUX}
              item={currentState.item}
              config={currentState.probableConfigs[currentState.index]}
            />
          )}

        <StyledDrawer
          open={!!wheelType}
          onBackdropClick={close}
          onClose={close}
          keepMounted={hasOpened}
        >
          <Odds
            sport={sport}
            onClose={close}
            onWheelTypeChange={setWheelType}
            wheelType={wheelType}
            rarity={rarity}
          />
        </StyledDrawer>
        {!!isTabletAndAbove && footer}
      </Grid>
      {!isTabletAndAbove && footer}
      {!!isTabletAndAbove && !!showRaritySwitcher && (
        <SwitcherWrapper
          style={{ y: uiSpring.down, opacity: uiSpring.opacity }}
        >
          <WheelRaritySwitcher rarity={rarity} />
        </SwitcherWrapper>
      )}
    </Wrapper>
  );
};

export const Wheel = ({ rarity }: Props) => {
  return (
    <Root>
      <WheelComponent
        rarity={rarity}
        // reset wheelState when rarity changes
        key={rarity}
      />
    </Root>
  );
};
